import React from 'react';
import { formatNumber } from '@/utils/formatNumber';
import { BarChart3, Zap, Sun, TrendingDown, Activity, Gauge } from 'lucide-react';

interface SummaryBoxProps {
  totalConsumption: number;
  gridImport: number;
  solarConsumption: number;
  autopilotSavings: number;
  peakDemand: number;
  avgDailyConsumption: number;
  gridPercent: number;
  solarPercent: number;
  savingsPercent: number;
  totalBaseline?: number;
  totalDays?: number;
  dateRange?: string;
}

export const SummaryBox: React.FC<SummaryBoxProps> = ({
  totalConsumption,
  gridImport,
  solarConsumption,
  autopilotSavings,
  peakDemand,
  avgDailyConsumption,
  gridPercent,
  solarPercent,
  savingsPercent,
  totalBaseline = 0,
  totalDays = 0,
  dateRange = '',
}) => {
  // Simple safety checks
  const safeGridPercent = (isNaN(gridPercent) || !isFinite(gridPercent)) ? 0 : gridPercent;
  const safeSolarPercent = (isNaN(solarPercent) || !isFinite(solarPercent)) ? 0 : solarPercent;
  const safeSavingsPercent = (isNaN(savingsPercent) || !isFinite(savingsPercent)) ? 0 : savingsPercent;
  const safeGridImport = (isNaN(gridImport) || !isFinite(gridImport)) ? 0 : gridImport;
  const safeSolarConsumption = (isNaN(solarConsumption) || !isFinite(solarConsumption)) ? 0 : solarConsumption;
  const safeAutopilotSavings = (isNaN(autopilotSavings) || !isFinite(autopilotSavings)) ? 0 : autopilotSavings;
  const safeTotalConsumption = (isNaN(totalConsumption) || !isFinite(totalConsumption)) ? 0 : totalConsumption;
  return (
    <div className="bg-white rounded-lg shadow-sm border border-light-grey p-4 w-full h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className="h-[18px] w-[18px] bg-light-grey rounded flex items-center justify-center">
            <BarChart3 className="h-3 w-3 text-primary" />
          </div>
          <h3 className="text-dark-blue heading-7">Energy Summary</h3>
        </div>
        {(totalDays > 0 || dateRange) && (
          <div className="text-[10px] text-dark-grey">
            {totalDays > 0 && <span>{totalDays} days</span>}
            {totalDays > 0 && dateRange && <span> • </span>}
            {dateRange && <span>{dateRange}</span>}
          </div>
        )}
      </div>
      
      <div className="flex-1 flex flex-col space-y-2">
        {/* Row 1: Total Consumption */}
        <div className="flex justify-between items-center">
          <span className="paragraph-10 font-medium text-dark-grey">Total Consumption</span>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-dark-blue">{formatNumber(safeTotalConsumption, 0)}</span>
            <span className="paragraph-12 text-black">kWh</span>
          </div>
        </div>
        
        {/* Row 2: Portion Values - Grid, Solar, Autopilot - Enhanced */}
        <div className="grid grid-cols-3 gap-2 p-2.5 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-light-grey">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <div className="h-6 w-6 bg-purple-100 rounded flex items-center justify-center">
                <Zap className="h-3 w-3 text-purple-600" />
              </div>
              <span className="text-[11px] font-semibold text-dark-grey">Grid</span>
            </div>
            <div className="flex items-baseline justify-center gap-0.5">
              <span className="text-base font-bold text-black">{formatNumber(safeGridImport, 0)}</span>
              <span className="text-[10px] font-medium text-purple-600">kWh</span>
            </div>
          </div>
          
          <div className="text-center border-x border-light-grey">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <div className="h-6 w-6 bg-green-100 rounded flex items-center justify-center">
                <Sun className="h-3 w-3 text-green-600" />
              </div>
              <span className="text-[11px] font-semibold text-dark-grey">Solar</span>
            </div>
            <div className="flex items-baseline justify-center gap-0.5">
              <span className="text-base font-bold text-black">{formatNumber(safeSolarConsumption, 0)}</span>
              <span className="text-[10px] font-medium text-green-600">kWh</span>
            </div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <div className="h-6 w-6 rounded flex items-center justify-center">
                <TrendingDown className="h-3 w-3 text-dark-grey" />
              </div>
              <span className="text-[11px] font-semibold text-dark-grey">Autopilot</span>
            </div>
            <div className="flex items-baseline justify-center gap-0.5">
              <span className="text-base font-bold text-blue-600">{formatNumber(safeAutopilotSavings, 0)}</span>
              <span className="text-[10px] font-medium text-blue-600">kWh</span>
            </div>
          </div>
        </div>
        
        {/* Row 3: Bar Charts */}
        <div className="space-y-1.5 pt-1">
          {(() => {
            // Calculate total energy sources and relative widths
            const totalEnergySources = safeGridImport + safeSolarConsumption;
            const safeTotalConsumption = isNaN(totalConsumption) || !isFinite(totalConsumption) ? 0 : totalConsumption;
            const safeBaseline = isNaN(totalBaseline) || !isFinite(totalBaseline) ? 0 : totalBaseline;
            
            // Both bars use total consumption as reference (100%)
            // This allows direct visual comparison
            const maxBarWidth = 100;
            
            // Energy sources bar width = 100% (represents total consumption)
            const energySourcesWidth = maxBarWidth;
            
            // Autopilot bar width is proportional to baseline/total consumption
            // Width = (totalBaseline / totalConsumption) * 100
            const autopilotBarWidth = safeTotalConsumption > 0 ? (safeBaseline / safeTotalConsumption) * maxBarWidth : 0;
            
            // Calculate the savings portion within the autopilot bar
            // This shows how much of the baseline was saved
            const savingsPortionWidth = safeBaseline > 0 ? (safeAutopilotSavings / safeBaseline) * 100 : 0;
            
            return (
              <>
                {/* Energy Sources Bar Chart */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="paragraph-9 font-medium text-dark-grey">Energy Sources</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-medium text-black">{formatNumber(totalEnergySources, 0)} kWh</span>
                      <span className="text-xs text-dark-grey">({formatNumber(safeGridPercent, 1)}% Grid / {formatNumber(safeSolarPercent, 1)}% Solar)</span>
                    </div>
                  </div>
                  <div className="h-6 relative">
                    <div className="absolute inset-0 bg-gray-100 rounded-md shadow-inner"></div>
                    <div 
                      className="absolute top-0 left-0 h-full flex rounded-md overflow-hidden transition-all duration-500"
                      style={{ width: `${energySourcesWidth}%` }}
                    >
                      <div
                        className="flex items-center justify-center transition-all duration-500 ease-out relative"
                        style={{
                          width: `${safeGridPercent}%`,
                          background: 'linear-gradient(135deg, #85348C 0%, #6b2870 100%)'
                        }}
                      >
                        {safeGridPercent > 15 && (
                          <span className="text-white font-semibold text-[9px] drop-shadow-sm">Grid</span>
                        )}
                      </div>
                      <div
                        className="flex items-center justify-center transition-all duration-500 ease-out relative"
                        style={{
                          width: `${safeSolarPercent}%`,
                          background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                        }}
                      >
                        {safeSolarPercent > 15 && (
                          <span className="text-white font-semibold text-[9px] drop-shadow-sm">Solar</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Autopilot Savings Bar Chart */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="paragraph-9 font-medium text-dark-grey">Autopilot Breakdown</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-medium text-black">{formatNumber(safeBaseline, 0)} kWh total</span>
                      <span className="text-xs text-dark-grey">
                        ({formatNumber(100 - safeSavingsPercent, 1)}% Actual / {formatNumber(safeSavingsPercent, 1)}% Savings)
                      </span>
                    </div>
                  </div>
                  <div className="h-6 relative">
                    <div className="absolute inset-0 bg-gray-100 rounded-md shadow-inner"></div>
                    <div 
                      className="absolute top-0 left-0 h-full flex rounded-md overflow-hidden transition-all duration-500"
                      style={{ width: `${autopilotBarWidth}%` }}
                    >
                      {/* Actual consumption (after savings) */}
                      <div
                        className="flex items-center justify-center transition-all duration-500 ease-out relative"
                        style={{
                          width: `${100 - safeSavingsPercent}%`,
                          background: 'linear-gradient(135deg, #0E7EE4 0%, #0969c3 100%)'
                        }}
                      >
                        {(100 - safeSavingsPercent) > 15 && (
                          <span className="text-white font-semibold text-[9px] drop-shadow-sm">Actual</span>
                        )}
                      </div>
                      {/* Savings portion */}
                      <div
                        className="flex items-center justify-center transition-all duration-500 ease-out relative"
                        style={{
                          width: `${safeSavingsPercent}%`,
                          background: 'linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%)'
                        }}
                      >
                        {safeSavingsPercent > 15 && (
                          <span className="text-white font-semibold text-[9px] drop-shadow-sm">Savings</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
        
        {/* Row 4: Peak Demand and Avg Daily */}
        <div className="grid grid-cols-2 gap-3 pt-1 border-t border-light-grey">
          <div className="flex justify-between items-baseline">
            <span className="paragraph-10 text-dark-grey">Peak Demand</span>
            <div className="flex items-baseline gap-0.5">
              <span className="text-sm font-semibold">{formatNumber(peakDemand, 1)}</span>
              <span className="paragraph-10 text-black">kW</span>
            </div>
          </div>
          
          <div className="flex justify-between items-baseline">
            <span className="paragraph-10 text-dark-grey">Avg Daily</span>
            <div className="flex items-baseline gap-0.5">
              <span className="text-sm font-semibold">{formatNumber(avgDailyConsumption, 0)}</span>
              <span className="paragraph-10 text-black">kWh/day</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};